// Pure date helpers shared by the desktop CalendarPanel, the mobile DateSheet
// and every date formatter. No React, no DOM, no component imports — so
// components/primitives.tsx and the sheets both depend on this file instead
// of each other (mobile overhaul Phase 0 review: no ESM cycle). primitives.tsx
// re-exports everything here, so existing call sites keep importing from it.

export const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// en-AU ICU spells short June/July as "June"/"July" — the mockups use strict
// three-letter months, so format by hand.
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const DP_DOW = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];


/** "Thu 12 Jun" — page-header date chip. */
export function todayLabel(): string {
  const d = new Date();
  return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

export function isoOf(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export function shiftDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return isoOf(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

/** Month arrows, wrapping the year. Shared with the mobile DateSheet. */
export function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  let y = year;
  let m = month + delta;
  if (m < 1) {
    m = 12;
    y -= 1;
  } else if (m > 12) {
    m = 1;
    y += 1;
  }
  return { year: y, month: m };
}

/** The Mon-anchored 6×7 grid of ISO dates covering the given month — the
    desktop panel and the mobile DateSheet render the same 42 cells. */
export function monthGrid(year: number, month: number): string[] {
  const first = new Date(`${isoOf(year, month, 1)}T00:00:00`);
  const lead = (first.getDay() + 6) % 7; // days shown from the prior month
  const gridStart = shiftDays(isoOf(year, month, 1), -lead);
  return Array.from({ length: 42 }, (_, i) => shiftDays(gridStart, i));
}

/** "#0231" — project numbers share the invoice zero-padding. */
export function formatProjectNumber(n: number | null | undefined): string | null {
  if (n === null || n === undefined) return null;
  return `#${String(n).padStart(4, '0')}`;
}

/** "28 Jun" — short day-month per the mockups (card dues, row metas). */
export function formatDayMonth(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** "28 Jun 2026" — full date for detail fields and stamps. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "Fri 13 Jun" — weekday + day-month (follow-up banner). */
export function formatWeekdayDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** "Mon 3" — weekday + bare day number, no month (crew half-day chip row,
    mockups-feature-builds-2026-07.html §4b — a booking's date range is
    short enough that month context already lives elsewhere in the form). */
export function formatWeekdayDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return `${DAYS[d.getDay()]} ${d.getDate()}`;
}

/** ISO date n days from the given ISO date. */
export function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${String(d.getDate()).padStart(2, '0')}`;
}
